import {getPictureUri} from "../../../fundamentals/CCOPicture";
import {stripTreeText} from "../../../../utility/componentUtil";
import {CmTaxonomy} from "../../../../../packages/graphql-layer/src";
import {CmTaxonomyType} from "../../../types";
import {SchemaProps} from "./JsonLdSchemaRouter";
import {getCompleteDescription, normalizeCanonicalUrl} from "../headUtils";

// news stuff
export const newsAuthor = {
    "author": {
        "@type": "Organization",
        "name": "SOS-Kinderdorf e.V."
    }
}
export const articleSection = (props: SchemaProps) => {
    const nav = props.page?.navigationPath;
    if (nav && nav.length > 0) {
        const length = nav.length;
        return length > 2 ? nav[length - 2].title : nav[length - 1].title;
    } else {
        return "News";
    }
}
export const keywords = (props: SchemaProps) => {
    let keywords: string[] = [];
    if (props.page?.keywords) {
        keywords = props.page.keywords.split(",");
    }
    if (props.page?.locationTaxonomy && props.page.locationTaxonomy.length > 0) {
        props.page.locationTaxonomy.forEach((location: CmTaxonomy) => {
            const tax: CmTaxonomyType = location as CmTaxonomyType;
            if (tax && tax.value && tax.value.length > 0) {
                keywords.push(tax.value);
            }
        })
    }
    if (props.page?.subjectTaxonomy && props.page.subjectTaxonomy.length > 0) {
        props.page.subjectTaxonomy.forEach((subject: CmTaxonomy) => {
            const tax: CmTaxonomyType = subject as CmTaxonomyType;
            if (tax && tax.value && tax.value.length > 0) {
                keywords.push(tax.value);
            }
        })
    }
    return keywords;
}
export const modificationDate = (props: SchemaProps) => {
    const date = props.page?.modificationDate;
    return formatDate(date);
}
export const creationDate = (props: SchemaProps) => {
    const date = props.page?.creationDate;
    return formatOnlyDate(date);
}
const formatOnlyDate = (date: string | null | undefined) => {
    if (!date) return "";
    const formattedDate = date.split("[")[0]; // "2025-01-22T15:47Z"
    return formattedDate.split("T")[0]; // "2025-01-22"
}
const formatDate = (date: string | null | undefined) => {
    if (!date) return "";
    let formattedDate = date.split("[")[0]; // "2025-01-22T15:47Z"
    if (formattedDate.endsWith("Z")) {
        formattedDate = formattedDate.substring(0, formattedDate.length - 1); // "2025-01-22T15:47"
    }
    const parts = formattedDate.split("T");
    if (parts.length === 2) {
        const timeParts = parts[1].split(":");
        if (timeParts.length === 2) {
            formattedDate += ":00";
        }
    }
    return formattedDate + "Z";
}
// publication stuff
export const organization = "Organization";
export const publicationTitle = (props: SchemaProps) => {
    return props.page?.title || '';
}

function getAuthorName(author: any) {
    let name = '';
    if (author.authortitle) {
        name += author.authortitle;
        if (!name.endsWith(' ')) {
            name += ' ';
        }
    }
    if (author.name) {
        name += author.name;
        name += ' ';
    }
    if (author.surname) {
        name += author.surname;
    }
    return name;
}

export const publicationEditor = (props: SchemaProps) => {
    const hasEditor = props.page.publisher;
    if (hasEditor) {
        return {
            "editor": {
                "@type": "Organization",
                "name": props.page.publisher
            }
        }
    }

    const hasAuthor = props.page.authors && props.page.authors.length > 0;
    const hasSingleAuthor = hasAuthor && props.page.authors.length === 1;
    if (hasSingleAuthor && !props.page.authors[0].name) {
        return {
            "editor": {
                "@type": "Organization",
                "name": props.page.authors[0].surname
            }
        }
    }
    return undefined;
}

export const publicationAuthor = (props: SchemaProps) => {
    const hasAuthor = props.page.authors && props.page.authors.length > 0;
    const hasSingleAuthor = hasAuthor && props.page.authors.length === 1;

    if (hasAuthor) {
        if (hasSingleAuthor) {
            if (props.page.authors[0].name) {
                return {
                    "author": {
                        "@type": "Person",
                        "name": getAuthorName(props.page.authors[0])
                    }
                }
            }
        } else {
            return {
                "author": props.page.authors.map((a: any) => ({
                    "@type": "Person",
                    "name": getAuthorName(a)
                }))
            }
        }
    }
    return undefined;
}

export const publicationPublisher = () => {
    return {
        "publisher": {
            "@type": "Organization",
            "name": "SOS-Kinderdorf e.V."
        }
    }
}

export const publicationOrderInfo = (props: SchemaProps) => {
    const canOrder = props?.page?.ordable && props.page.download && props.page.download.length > 0;
    if (canOrder) {
        const orderUrl = `https://www.sos-kinderdorf.de${props.page.download[0].data.uri}`;
        return {
            "workExample": [
                {
                    "@type": "Book",
                    "bookFormat": "EBook",
                    "encodingFormat": "application/pdf",
                    "url": orderUrl
                },
                {
                    "@type": "Book",
                    "bookFormat": "Paperback"
                }
            ]
        }
    } else {
        return {
            "encodingFormat": "application/pdf"
        }
    }
}

// product stuff
export const currency = "EUR";
export const product = "#product";
export const productName = (props: SchemaProps) => {
    return props.page?.teaserTitle || props.page?.title || '';
}
export const productPrice = (props: SchemaProps) => {
    const price = props.page?.price || '';
    return price.replace(',', '.');
}
export const productDescription = (props: SchemaProps) => {
    return stripTreeText(props.page?.teaserText?.textAsTree).trim() ? stripTreeText(props.page.teaserText.textAsTree).trim() : '';
}
// generic stuff
export const logo = {
    "@type": "ImageObject",
    "url": "https://www.sos-kinderdorf.de/logo-digitalgruen.svg"
}
export const publisher = {
    "@type": organization,
    "name": "SOS-Kinderdorf e.V.",
    "logo": {
        "@type": "ImageObject",
        "url": "https://www.sos-kinderdorf.de/logo-digitalgruen.svg"
    }
}
export const pictureUri = (props: SchemaProps) => {
    if (!props.page?.picture) return undefined;
    return "https://www.sos-kinderdorf.de" + getPictureUri("320", false, props.page.picture);
}
export const countryIso = "DE";
export const canocicalUrl = (props: SchemaProps) => {
    const url = props.page?.canonicalUrl || '';
    return url ? normalizeCanonicalUrl(url) : url;
}
export const title = (props: SchemaProps) => {
    return props.page?.title?.trim() || props.page?.htmlTitle?.trim() || 'Einrichtung SOS-Kinderdorf';
}
export const description = (props: SchemaProps) => {
    return getCompleteDescription(props.page, props.caeGlobalSettings);
}
export const phone = (props: SchemaProps) => {
    return props.page?.pageSettings?.['sos-phone'] || "+49 89 12606-0";
}
export const email = (props: SchemaProps) => {
    return props.page?.pageSettings?.['sos-email'] || "info@sos-kinderdorf.de";
}
const street = (props: SchemaProps) => {
    return props.page?.pageSettings?.['sos-street'] || "";
}
const zip = (props: SchemaProps) => {
    return props.page?.pageSettings?.['sos-zip'] || "";
}
const city = (props: SchemaProps) => {
    return props.page?.pageSettings?.['sos-city'] || "";
}
export const orgaAddress = (props: SchemaProps) => {
    const streetStr = street(props);
    const zipStr = zip(props);
    const cityStr = city(props);
    const combinedAddress = {
        "address": {
            "@type": "PostalAddress",
            "streetAddress": streetStr,
            "postalCode": zipStr,
            "addressLocality": cityStr,
            "addressCountry": countryIso
        }
    }
    return (streetStr && zipStr && cityStr) ? combinedAddress : undefined;
}
// Homepage stuff
export const areaServed = {
    "@type": "Country",
    "name": "Deutschland"
};
export const rootOrgaId = "https://www.sos-kinderdorf.de/#organization";
export const rootName = "SOS-Kinderdorf e.V.";
export const rootUrl = "https://www.sos-kinderdorf.de/";
export const rootDescription = "SOS-Kinderdorf e.V. ist ein gemeinnütziger, bundesweit tätiger Träger der Kinder- und Jugendhilfe, der sich seit Jahrzehnten für benachteiligte Kinder, Jugendliche und Familien in Deutschland einsetzt.";
export const context = "https://schema.org";
export const sameAs = [
    "https://www.facebook.com/SOSKinderdorfDE/",
    "https://www.instagram.com/soskinderdorfde/",
    "https://www.youtube.com/user/soskinderdorf",
    "https://www.linkedin.com/company/sos-kinderdorf-e.v./",
    "https://de.wikipedia.org/wiki/SOS-Kinderdorf_Deutschland"
]
export const rootAddress = {
    "@type": "PostalAddress",
    "streetAddress": "Renatastraße 77",
    "postalCode": "80639",
    "addressLocality": "München",
    "addressCountry": countryIso
}
export const founder = {
    "@type": "Person",
    "name": "Hermann Gmeiner"
};
export const foundingDate = "1955";
// types
export const type_news = "NewsArticle";
export const type_ngo = "NGO";
export const type_article = "Article";
export const type_product = "Product";
export const type_book = "Book";